/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Select, InputPrice, Loading, Datepicker, HelpIcon} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {allCompanyLookup, projectLookup, basicLookup} from '../../../redux/slices/lookup.slice'
import {guaranteeletterCreate, setRedirect} from '../../../redux/slices/guaranteeletter.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  Bank: state.lookup.Bank,
  GuaranteeLetterType: state.lookup.GuaranteeLetterType,
  GuaranteeLetterState: state.lookup.GuaranteeLetterState,
  GuaranteeLetterExistence: state.lookup.GuaranteeLetterExistence,
  loading: state.lookup.loading
})

const selectGuaranteeLetterData = (state) => ({
  isRedirect: state.guaranteeletter.isRedirect,
  loading: state.guaranteeletter.loading
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  
  const pageUrl = useMemoizedPageUrl(location)

  const {
    projectLookupList,
    companyLookup,
    Bank,
    GuaranteeLetterType,
    GuaranteeLetterState,
    GuaranteeLetterExistence,
    loading,
  } = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectGuaranteeLetterData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
    dispatch(basicLookup('Bank'))
    dispatch(basicLookup('GuaranteeLetterType'))
    dispatch(basicLookup('GuaranteeLetterState'))
    dispatch(basicLookup('GuaranteeLetterExistence'))
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    CompanyId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    BankId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    Branch: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    GuaranteeLetterTypeId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    GuaranteeLetterStateId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    GuaranteeLetterNo: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    ProjectId: null,
    CompanyId: null,
    BankId: null,
    Branch: '',
    Name: '',
    GuaranteeLetterTypeId: '',
    GuaranteeLetterStateId: '',
    GuaranteeLetterExistenceId: '',
    GuaranteeLetterNo: '',
    EditDate: new Date(),
    DefermentDate: null,
    CommissionDate: null,
    DeliveryDate: null,
    Beneficiary: '',
    OriginalAmount: 0,
    BankCommission: 0,
    Balance: 0,
    FinancialRate: 0,
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(guaranteeletterCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.GUARATEELETTER'}),
      path: '/cash/guaranteeletter',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect && isRedirect.id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
      dispatch(setRedirect(false))
    }
  }, [isRedirect, navigate, pageUrl, dispatch])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.GUARATEELETTER.ADD'})}
      </PageTitle>
      <KTCard>
        <KTCardHeader title={intl.formatMessage({id: 'BC.GUARATEELETTER.ADD'})}>
          <HelpIcon articleId='guarantee-letter-create' />
        </KTCardHeader>
        <KTCardBody>
        <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.NAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Name')}
                />
                {formik.touched.Name && formik.errors.Name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.PROJECTNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={projectLookupList}
                  required
                  {...formik.getFieldProps('ProjectId')}
                  onChange={(item) => formik.setFieldValue('ProjectId', Number(item?.value))}
                />
                {formik.touched.ProjectId && formik.errors.ProjectId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.ProjectId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.COMPANYNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={companyLookup}
                  required
                  {...formik.getFieldProps('CompanyId')}
                  onChange={(item) => formik.setFieldValue('CompanyId', Number(item?.value))}
                />
                {formik.touched.CompanyId && formik.errors.CompanyId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.CompanyId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BANKNAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={Bank}
                  required
                  {...formik.getFieldProps('BankId')}
                  onChange={(item) => formik.setFieldValue('BankId', Number(item?.value))}
                />
                {formik.touched.BankId && formik.errors.BankId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.BankId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BRANCH'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  required
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Branch')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.GUARATEELETTERTYPENAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={GuaranteeLetterType}
                  required
                  {...formik.getFieldProps('GuaranteeLetterTypeId')}
                  onChange={(item) =>
                    formik.setFieldValue('GuaranteeLetterTypeId', Number(item?.value))
                  }
                />
                {formik.touched.GuaranteeLetterTypeId && formik.errors.GuaranteeLetterTypeId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.GuaranteeLetterTypeId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.GUARATEELETTERSTATENAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={GuaranteeLetterState}
                  required
                  {...formik.getFieldProps('GuaranteeLetterStateId')}
                  onChange={(item) =>
                    formik.setFieldValue('GuaranteeLetterStateId', Number(item?.value))
                  }
                />
                {formik.touched.GuaranteeLetterStateId && formik.errors.GuaranteeLetterStateId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.GuaranteeLetterStateId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.GUARATEELETTERNO'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('GuaranteeLetterNo')}
                />
                {formik.touched.GuaranteeLetterNo && formik.errors.GuaranteeLetterNo && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.GuaranteeLetterNo}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.GUARANTEELETTEREXISTENCE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={GuaranteeLetterExistence}
                  required
                  {...formik.getFieldProps('GuaranteeLetterExistenceId')}
                  onChange={(item) =>
                    formik.setFieldValue('GuaranteeLetterExistenceId', Number(item?.value))
                  }
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.EDITDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('EditDate')}
                  onChange={([date]) => formik.setFieldValue('EditDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.DEFERMENTDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('DefermentDate')}
                  onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BENEFICIARY'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Beneficiary')}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.ORIGINALAMOUNT'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <InputPrice
                  {...formik.getFieldProps('OriginalAmount')}
                  onChange={(value) => formik.setFieldValue('OriginalAmount', Number(value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BANKCOMISSION'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <InputPrice
                  {...formik.getFieldProps('BankCommission')}
                  onChange={(value) => formik.setFieldValue('BankCommission', Number(value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.COMMISSIONDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('CommissionDate')}
                  onChange={([date]) => formik.setFieldValue('CommissionDate', date)}
                />

                <div className='d-flex gap-2 mt-3'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      const date = new Date()
                      date.setMonth(date.getMonth() + 1)
                      formik.setFieldValue('CommissionDate', date)
                    }}
                  >
                    1 {intl.formatMessage({id: 'GENERAL.MONTH'})}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      const date = new Date()
                      date.setMonth(date.getMonth() + 3)
                      formik.setFieldValue('CommissionDate', date)
                    }}
                  >
                    3 {intl.formatMessage({id: 'GENERAL.MONTH'})}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      const date = new Date()
                      date.setMonth(date.getMonth() + 6)
                      formik.setFieldValue('CommissionDate', date)
                    }}
                  >
                    6 {intl.formatMessage({id: 'GENERAL.MONTH'})}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      const date = new Date()
                      date.setFullYear(date.getFullYear() + 1)
                      formik.setFieldValue('CommissionDate', date)
                    }}
                  >
                    1 {intl.formatMessage({id: 'GENERAL.YEAR'})}
                  </button>
                </div>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.DELIVERYDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('DeliveryDate')}
                  onChange={([date]) => formik.setFieldValue('DeliveryDate', date)}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BALANCE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <InputPrice
                  {...formik.getFieldProps('Balance')}
                  onChange={(value) => formik.setFieldValue('Balance', Number(value))}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'GUARATEELETTER.CREATE.FINANCIALRATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='number'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('FinancialRate')}
                  onFocus={(event) => event.target.select()}
                />
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
